import React, { useState, useRef, useEffect } from 'react'
import { HiChevronDown, HiCheck } from 'react-icons/hi'

const CURRENCIES = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'HUF', symbol: 'Ft', name: 'Hungarian Forint' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
]

function CurrencySelector({ value, onChange, disabled = false }) {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef(null)

    const selectedCurrency = CURRENCIES.find(c => c.code === value) || null

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleSelect = (currency) => {
        onChange(currency.code)
        setIsOpen(false)
    }

    const handleClear = (e) => {
        e.stopPropagation()
        onChange(null)
        setIsOpen(false)
    }

    return (
        <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block', zIndex: 100 }}>
            <button
                type="button"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                disabled={disabled}
                className="currency-selector-button"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.625rem 1rem',
                    background: 'var(--glass-bg)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: isOpen ? '2px solid var(--accent-primary)' : '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-md)',
                    color: selectedCurrency ? 'var(--text-primary)' : 'var(--text-muted)',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    minWidth: '140px',
                    opacity: disabled ? 0.5 : 1,
                    boxShadow: isOpen ? 'var(--glow-primary)' : 'var(--shadow-sm)'
                }}
                onMouseEnter={(e) => {
                    if (!disabled && !isOpen) {
                        e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.5)'
                        e.currentTarget.style.boxShadow = '0 0 12px rgba(59, 130, 246, 0.2)'
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isOpen) {
                        e.currentTarget.style.borderColor = 'var(--glass-border)'
                        e.currentTarget.style.boxShadow = 'var(--shadow-sm)'
                    }
                }}
            >
                {selectedCurrency ? (
                    <>
                        <span style={{ fontWeight: '600' }}>{selectedCurrency.symbol}</span>
                        <span>{selectedCurrency.code}</span>
                    </>
                ) : (
                    <span>No currency</span>
                )}
                <HiChevronDown style={{
                    marginLeft: 'auto',
                    transition: 'transform 0.2s ease',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                }} />
            </button>

            {isOpen && (
                <div
                    className="currency-dropdown"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        minWidth: '200px',
                        background: 'rgba(26, 26, 26, 0.95)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: '1px solid var(--glass-border)',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: 'var(--shadow-xl), 0 0 30px rgba(0, 0, 0, 0.3)',
                        zIndex: 1000,
                        maxHeight: '280px',
                        overflowY: 'auto',
                        animation: 'fadeIn 0.15s ease-out'
                    }}
                >
                    {/* Clear option */}
                    <div
                        onClick={handleClear}
                        style={{
                            padding: '0.625rem 1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--border-color)',
                            color: 'var(--text-muted)',
                            transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)'
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent'
                        }}
                    >
                        <span style={{ opacity: 0.5 }}>—</span>
                        <span>No currency</span>
                        {!selectedCurrency && <HiCheck style={{ marginLeft: 'auto', color: 'var(--accent-primary)' }} />}
                    </div>

                    {CURRENCIES.map((currency) => (
                        <div
                            key={currency.code}
                            onClick={() => handleSelect(currency)}
                            style={{
                                padding: '0.625rem 1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                cursor: 'pointer',
                                background: selectedCurrency?.code === currency.code ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                                transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = selectedCurrency?.code === currency.code ? 'rgba(59, 130, 246, 0.15)' : 'transparent'
                            }}
                        >
                            <span style={{ fontWeight: '600', minWidth: '24px', color: 'var(--accent-secondary)' }}>
                                {currency.symbol}
                            </span>
                            <span style={{ fontWeight: '500' }}>{currency.code}</span>
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginLeft: '0.25rem' }}>
                                {currency.name}
                            </span>
                            {selectedCurrency?.code === currency.code && (
                                <HiCheck style={{ marginLeft: 'auto', color: 'var(--accent-primary)' }} />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default CurrencySelector
export { CURRENCIES }
